// src/components/BrokersGrid.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  IconButton,
  Tooltip,
  CircularProgress
} from "@mui/material";
import {
  History as HistoryIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Upload as UploadIcon,
  Download as DownloadIcon
} from "@mui/icons-material";

import api, { getBrokerEntries, saveBrokers, deleteBrokerEntry } from "../api";
import { useCanWritePricing, NO_PRICING_WRITE_MESSAGE } from "../permissions";
import BrokerHistory from "./BrokerHistory";
import { addTrackedChanges, keyEditModifiedDate, mergeUnsavedEdits, restoreBlankValues, trackCellEdit, withBlankBaseline } from "../utils/modifiedDates";
import {
  assignKeyEditId,
  duplicateKeysMessage,
  findDuplicateKeys,
  makeTrackingKeyOf,
  withoutClientFields,
} from "../utils/rowIdentity";
import {
  getWriteErrorMessage,
  isVersionConflict,
  withLoadedModifiedDate,
  nullToEmpty,
  ConfirmationDialog,
  DateTimeRenderer,
  useGridOperations,
  useBackgroundRefresh,
  PamGrid,
  QuickFilter,
  GridConfigMenu,
  useGridConfigs,
  normalizeBooleanValue as pamNormalizeBooleanValue,
  createColumnNormalizer,
  normalizeRowKeys,
  parseFileData,
  HistoryModal,
  isValueNull,
  isPermissionDenied,
  getPermissionDeniedText,
  getPermissionDeniedMessage,
  RequestAccessButton,
} from "@1823-partners/core";
import { formatDate } from "../utils/dateUtils";
import { sampleBrokerData } from "../data/sampleBrokerData";

// Broker-specific column mappings for flexible column name matching
const BROKER_COLUMN_MAPPINGS = {
  // Broker variations
  'broker': 'BROKER',
  'brokername': 'BROKER',
  'broker name': 'BROKER',
  'firm': 'BROKER',
  'firm name': 'BROKER',
  'dealer': 'BROKER',
  'counterparty': 'BROKER',

  // Price File variations (specific to broker grid)
  'price file': 'PRICE_FILE',
  'pricefile': 'PRICE_FILE',
  'filename': 'PRICE_FILE',
  'file name': 'PRICE_FILE',
  'file': 'PRICE_FILE',
  'data file': 'PRICE_FILE',
  'feed file': 'PRICE_FILE',

  // Source variations
  'source': 'SOURCE',
  'data source': 'SOURCE',
  'datasource': 'SOURCE',
  'feed type': 'SOURCE',
  'feedtype': 'SOURCE',
  'feed source': 'SOURCE',
  'system': 'SOURCE',

  // Email variations
  'email': 'EMAIL',
  'email address': 'EMAIL',
  'emailaddress': 'EMAIL',
  'e mail': 'EMAIL',
  'to email': 'EMAIL',
  'toemail': 'EMAIL',
  'contact email': 'EMAIL',
  'contactemail': 'EMAIL',
  'contact': 'EMAIL',

  // Subject variations
  'subject': 'SUBJECT',
  'email subject': 'SUBJECT',
  'emailsubject': 'SUBJECT',
  'message subject': 'SUBJECT',
  'messagesubject': 'SUBJECT',
  'title': 'SUBJECT',
  'topic': 'SUBJECT',

  // Security List variations
  'security list': 'SECURITY_LIST',
  'securitylist': 'SECURITY_LIST',
  'securities': 'SECURITY_LIST',
  'send securities': 'SECURITY_LIST',
  'sendsecurities': 'SECURITY_LIST',
  'include securities': 'SECURITY_LIST',
  'includesecurities': 'SECURITY_LIST',
  'securities flag': 'SECURITY_LIST',
  'sec list': 'SECURITY_LIST',

  // Additions variations
  'additions': 'ADDITIONS',
  'add': 'ADDITIONS',
  'additional': 'ADDITIONS',
  'send additions': 'ADDITIONS',
  'sendadditions': 'ADDITIONS',
  'include additions': 'ADDITIONS',
  'includeadditions': 'ADDITIONS',
  'additions flag': 'ADDITIONS',

  // CUSIPs variations
  'cusips': 'CUSIPS',
  'cusip': 'CUSIPS',
  'cusip list': 'CUSIPS',
  'cusiplist': 'CUSIPS',
  'securities list': 'CUSIPS',
  'securitieslist': 'CUSIPS',
  'identifiers': 'CUSIPS',
  'securities ids': 'CUSIPS',
  'ids': 'CUSIPS',

  // Notes variations
  'notes': 'NOTES',
  'note': 'NOTES',
  'comments': 'NOTES',
  'comment': 'NOTES',
  'remarks': 'NOTES',
  'remark': 'NOTES',

  // System field variations
  'modified by': 'MODIFIED_BY',
  'modifiedby': 'MODIFIED_BY',
  'last modified by': 'MODIFIED_BY',
  'lastmodifiedby': 'MODIFIED_BY',
  'updated by': 'MODIFIED_BY',
  'updatedby': 'MODIFIED_BY',
  'user': 'MODIFIED_BY',
  'username': 'MODIFIED_BY',
  'author': 'MODIFIED_BY',

  'modified date': 'MODIFIED_DATE',
  'modifieddate': 'MODIFIED_DATE',
  'last modified': 'MODIFIED_DATE',
  'lastmodified': 'MODIFIED_DATE',
  'last modified date': 'MODIFIED_DATE',
  'lastmodifieddate': 'MODIFIED_DATE',
  'updated date': 'MODIFIED_DATE',
  'updateddate': 'MODIFIED_DATE',
  'update date': 'MODIFIED_DATE',
  'updatedate': 'MODIFIED_DATE',
  'date modified': 'MODIFIED_DATE',
  'datemodified': 'MODIFIED_DATE',
  'timestamp': 'MODIFIED_DATE',
  'date time': 'MODIFIED_DATE',
  'datetime': 'MODIFIED_DATE'
};

// Create the column normalizer using 1823-core utility
const normalizeColumnNameForBroker = createColumnNormalizer(BROKER_COLUMN_MAPPINGS);

// The BROKERS writer key (also this grid's composite key, BROKER|PRICE_FILE).
const BROKER_KEY_FIELDS = ['BROKER', 'PRICE_FILE'];

const brokerCompositeKey = (row) => `${row.BROKER || ''}|${row.PRICE_FILE || ''}`;
// Unsaved edits and AG Grid row ids: TEMP_ROW_ID, else the KEY_EDIT_ID a
// renamed row carries, else BROKER|PRICE_FILE (see utils/rowIdentity).
const brokerTrackingKey = makeTrackingKeyOf(brokerCompositeKey);
// The key Save checks for duplicates; null while a key field is blank, which
// validateBrokerRow reports instead.
const brokerSaveKey = (row) =>
  BROKER_KEY_FIELDS.every((field) => String(row[field] ?? '').trim() !== '') ? brokerCompositeKey(row) : null;

const BrokersGrid = ({ userId }) => {
  const canWritePricing = useCanWritePricing();
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedEntryForHistory, setSelectedEntryForHistory] = useState(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [newRowCount, setNewRowCount] = useState(0);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [rowData, setRowData] = useState([]);
  // The rows of the latest render, for handlers that read the grid after an
  // await (the upload merge) rather than the rows their closure captured.
  const rowDataRef = useRef(rowData);
  rowDataRef.current = rowData;
  const [error, setError] = useState(null);
  // Read failures are separate from action failures so a recovered background
  // refresh can clear only its own banner without erasing a denied write.
  const [loadError, setLoadError] = useState(null);
  // The denial *object* alongside the message: RequestAccessButton reads
  // suggested_roles off it, which the message string cannot carry. Cleared
  // wherever `error` is cleared or replaced, or a later unrelated failure would
  // still show a button naming a role from an earlier denial.
  const [deniedError, setDeniedError] = useState(null);
  // A failed *background* refresh, tracked apart from `error`. The 30-second tick
  // must not overwrite a write failure the user still needs to act on, but it also
  // must not hide that the grid has stopped updating: if Beacon goes away, these
  // rows silently go stale. Separate state lets both facts show at once, which
  // conditioning the shared banner on deniedError could not — a generic save
  // failure is user-facing too and would still have been clobbered.
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [updatedRows, setUpdatedRows] = useState({}); // Dictionary to track rows with updated cells
  const theme = useTheme();
  const fileInputRef = useRef(null);
  // Every replacement of the action banner advances this token. A delayed clear
  // may only remove the exact error generation that scheduled it, so an older
  // validation/upload timer cannot erase a later permission denial.
  const actionErrorGenerationRef = useRef(0);
  // Set synchronously so a second Save click before React re-renders cannot
  // resend rows whose loaded MODIFIED_DATE the first request is about to spend.
  const isSavingRef = useRef(false);
  // Rows under an edited key: tracking key -> the key and MODIFIED_DATE they
  // had before, so editing the key back sends that MODIFIED_DATE again
  // (keyEditModifiedDate). Cleared with updatedRows.
  const loadedKeysRef = useRef({});

  const clearActionError = useCallback(() => {
    actionErrorGenerationRef.current += 1;
    setError(null);
    setDeniedError(null);
  }, []);

  const showActionError = useCallback((message, denial = null) => {
    actionErrorGenerationRef.current += 1;
    setError(message);
    setDeniedError(denial);
  }, []);

  const showTemporaryActionError = useCallback((message, timeoutMs) => {
    const generation = actionErrorGenerationRef.current + 1;
    actionErrorGenerationRef.current = generation;
    setError(message);
    setDeniedError(null);
    setTimeout(() => {
      if (actionErrorGenerationRef.current !== generation) return;
      actionErrorGenerationRef.current += 1;
      setError(null);
      setDeniedError(null);
    }, timeoutMs);
  }, []);

  // Use 1823-core grid operations hook for export and grid utilities
  const { gridRef, exportToExcel } = useGridOperations();

  // Per-user grid layout persistence. Own the hook here (rather than letting
  // GridConfigMenu instantiate it) so we can bridge gridConfigs.onGridReady to
  // PamGrid — without that bridge a slow grid mount can miss the default-layout
  // auto-apply. Reuses the same gridRef passed to <PamGrid ref=…/>.
  const gridConfigs = useGridConfigs(api, {
    app: 'pricing',
    gridId: 'brokers',
    gridApiRef: gridRef,
  });

  // Use 1823-core's normalizeBooleanValue for converting to actual booleans for UI
  // Note: local normalizeBooleanValue returns 'Y'/'N' for API, pamNormalizeBooleanValue returns true/false
  const convertToActualBoolean = pamNormalizeBooleanValue;

  // adoptServerModifiedDates: after a 409 the preserved edits move onto the
  // reloaded MODIFIED_DATEs, so saving again is the user's deliberate re-apply.
  // Otherwise edited rows keep the MODIFIED_DATE their edits were based on.
  const loadBrokers = useCallback(async (preserveLocalChanges = false, silent = false, { adoptServerModifiedDates = false } = {}) => {
    try {
      if (!silent) {
        setIsLoading(true);
      }
      // A silent load is the 30-second background refresh: it shows no spinner,
      // and it must not touch the banner either. Clearing would take a write
      // denial's Request access button away seconds after the user saw it.
      if (!silent) {
        setLoadError(null);
        clearActionError();
      }

      const response = await getBrokerEntries();
      const formattedData = response.data.map(entry => {
        // Convert all null values to empty strings
        const formattedEntry = nullToEmpty(entry);

        // Normalize boolean fields to actual boolean values for UI checkboxes
        if (formattedEntry['SECURITY_LIST'] !== undefined) {
          formattedEntry['SECURITY_LIST'] = convertToActualBoolean(formattedEntry['SECURITY_LIST']);
        }
        if (formattedEntry['ADDITIONS'] !== undefined) {
          formattedEntry['ADDITIONS'] = convertToActualBoolean(formattedEntry['ADDITIONS']);
        }

        // MODIFIED_DATE kept as-is for processing, formatted on display. The raw
        // blanks (null vs '') travel with the row so an untouched field is saved
        // as the server sent it.
        return withBlankBaseline(entry, formattedEntry);
      });

      if (preserveLocalChanges) {
        // Use blotter-style preservation logic - simpler and more robust
        setUpdatedRows(currentUpdatedRows => {
          setRowData(currentRowData => {
            // If preserving local changes, merge them back into the fresh data
            let finalData = formattedData;

            if (Object.keys(currentUpdatedRows).length > 0 || currentRowData.some(row => row.TEMP_ROW_ID)) {
              // First, apply unsaved changes to existing server data. A row whose
              // key was edited is kept whole, since only its edited fields are tracked.
              const updatedServerData = mergeUnsavedEdits({
                freshRows: formattedData,
                currentRows: currentRowData,
                changesByKey: currentUpdatedRows,
                trackingKeyOf: brokerTrackingKey,
                keyFields: BROKER_KEY_FIELDS,
                adoptServerModifiedDates,
              });

              // Collect all new rows that need to be preserved (preserve as-is from currentRowData)
              const preservedNewRows = [];
              currentRowData.forEach(existingRow => {
                if (existingRow.TEMP_ROW_ID) {
                  // New rows should always be preserved since they don't exist in server data
                  preservedNewRows.push(existingRow); // Preserve exactly as-is
                }
              });

              // Put new rows at the top, followed by server data
              finalData = [...preservedNewRows, ...updatedServerData];
            }

            return finalData;
          });

          // Return the preserved tracking state
          return currentUpdatedRows;
        });
      } else {
        setRowData(formattedData);
        setUpdatedRows({});
        loadedKeysRef.current = {};
      }
      // Clear read-only banners only after the response has been processed into
      // fresh rows; receiving an unusable response is not a successful refresh.
      setLoadError(null);
      setRefreshFailed(false);
    } catch (error) {
      console.error("Error fetching brokers:", error);
      // Same rule on the way out: a background refresh must not replace what the
      // user is reading. Setting the message here while a write's denial object
      // stayed would also pair a load error with a Request access button that has
      // nothing to do with it.
      if (silent) {
        // Say the data is stale without disturbing whatever the banner holds.
        setRefreshFailed(true);
      } else if (isPermissionDenied(error)) {
        setLoadError(getPermissionDeniedMessage(error));
      } else {
        setLoadError("Failed to load brokers. Please try again.");
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [convertToActualBoolean, clearActionError]);

  useEffect(() => {
    loadBrokers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Load brokers on mount

  // Background refresh every 30 seconds using 1823-core hook
  useBackgroundRefresh({
    fetchData: () => loadBrokers(true, true), // preserveLocalChanges=true, silent=true
    interval: 30000, // 30 seconds
    enabled: true
  });



  // Quick filter is now handled directly by QuickFilter component from 1823-core



  const handleViewHistory = useCallback((entryData) => {
    setSelectedEntryForHistory(entryData);
    setHistoryModalOpen(true);
  }, []);



  const handleDeleteEntry = useCallback((entryData) => {
    // Handle locally created new rows using TEMP_ROW_ID
    if (entryData.TEMP_ROW_ID) {
      setRowData(prev => prev.filter(row => row !== entryData));
      setUpdatedRows(prev => {
        const updated = { ...prev };
        delete updated[entryData.TEMP_ROW_ID];
        return updated;
      });
      return;
    }

    // Show confirmation modal for database entries
    setEntryToDelete(entryData);
    setConfirmationModalOpen(true);
  }, []);

  // Helper function to convert empty date strings to null
  const convertEmptyDatesToNull = useCallback((row) => {
    const dateFields = ['MODIFIED_DATE'];
    const cleanedRow = { ...row };

    dateFields.forEach(field => {
      if (cleanedRow[field] === '' || cleanedRow[field] === undefined) {
        cleanedRow[field] = null;
      }
    });

    return cleanedRow;
  }, []);

  // Helper function to check for duplicate CUSIPs in a CUSIP list
  const hasDuplicateCusips = useCallback((cusipsString) => {
    if (!cusipsString || cusipsString.trim() === '') {
      return false;
    }

    const cusipList = cusipsString.split(',').map(cusip => cusip.trim()).filter(cusip => cusip);
    const uniqueCusips = new Set(cusipList);

    return cusipList.length !== uniqueCusips.size;
  }, []);

  // Cell class function to highlight duplicate CUSIPs
  const getCusipCellClass = useCallback((params) => {
    if (params.colDef?.field === 'CUSIPS') {
      return hasDuplicateCusips(params.value) ? 'ag-cell-duplicate-cusips' : '';
    }
    return '';
  }, [hasDuplicateCusips]);

  const confirmDelete = useCallback(async () => {
    if (!entryToDelete) return;

    try {
      clearActionError();
      setSuccessMessage(null);
      setConfirmationModalOpen(false);

      // The tombstone carries the row's loaded MODIFIED_DATE unchanged; the
      // writer stamps MODIFIED_BY and the new MODIFIED_DATE.
      await deleteBrokerEntry(withLoadedModifiedDate(withoutClientFields(convertEmptyDatesToNull(entryToDelete))));

      // Remove from local state on successful deletion: this row only, so
      // another row with the same key keeps its place and its edits.
      const trackingKey = brokerTrackingKey(entryToDelete);
      setRowData(prev => prev.filter(row => brokerTrackingKey(row) !== trackingKey));
      setUpdatedRows(prev => {
        const updated = { ...prev };
        delete updated[trackingKey];
        return updated;
      });
      delete loadedKeysRef.current[trackingKey];

      setSuccessMessage('Entry deleted successfully.');
      setTimeout(() => setSuccessMessage(null), 3000);

    } catch (error) {
      console.error('Error deleting broker entry:', error);
      const writeError = getWriteErrorMessage(error, null);
      if (isPermissionDenied(error)) {
        showActionError(getPermissionDeniedText(error), error);
      } else if (writeError) {
        showActionError(writeError);
      } else {
        showTemporaryActionError('Failed to delete entry. Please try again.', 5000);
      }
      if (isVersionConflict(error)) {
        // Silent, so the conflict message stays on the banner.
        await loadBrokers(true, true, { adoptServerModifiedDates: true });
      }
    } finally {
      setEntryToDelete(null);
    }
  }, [entryToDelete, convertEmptyDatesToNull, loadBrokers, clearActionError, showActionError, showTemporaryActionError]);

  const cancelDelete = useCallback(() => {
    setConfirmationModalOpen(false);
    setEntryToDelete(null);
  }, []);

  const handleDuplicateEntry = useCallback((entryData) => {
    const nextRowCount = newRowCount + 1;
    setNewRowCount(nextRowCount);

    const duplicatedEntry = {
      TEMP_ROW_ID: `temp_${nextRowCount}_${Date.now()}`, // Add temporary tracking field
      BROKER: `new_row_${nextRowCount}`,
      CUSIPS: entryData.CUSIPS,
      PRICE_FILE: `new_file_${nextRowCount}`,
      EMAIL: entryData.EMAIL,
      SUBJECT: entryData.SUBJECT,
      SECURITY_LIST: entryData.SECURITY_LIST,
      ADDITIONS: entryData.ADDITIONS,
      SOURCE: entryData.SOURCE,
      // A new key: no loaded MODIFIED_DATE. The writer stamps both on save.
      MODIFIED_BY: null,
      MODIFIED_DATE: null
    };

    setRowData(prev => [duplicatedEntry, ...prev]);
  }, [newRowCount]);

  const getCellClass = useCallback((params) => {
    const data = params.data;
    const field = params.colDef?.field;

    if (!data || !field) return '';

    // Check if this is a new row using TEMP_ROW_ID
    if (data.TEMP_ROW_ID) {
      return 'ag-cell-updated-new-row';
    }

    // For existing rows, use the tracking key
    const rowChanges = updatedRows[brokerTrackingKey(data)];
    if (rowChanges && rowChanges[field]) {
      return 'ag-cell-updated';
    }

    return '';
  }, [updatedRows]);

  const columnDefs = useMemo(() => [
    {
      headerName: "Actions",
      headerTooltip: "Available actions for this entry: view history, edit, duplicate, or delete",
      field: "actions",
      cellStyle: { textAlign: "center" },
      maxWidth: 200,
      pinned: 'left',
      cellRenderer: (params) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Tooltip title="View History">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleViewHistory(params.data)}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Duplicate Entry">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => handleDuplicateEntry(params.data)}
            >
              <DuplicateIcon />
            </IconButton>
          </Tooltip>
          {/* Same gate as Save: /delete-*-entry requires pricing.overrides:write, not a separate
              delete permission. The <span> is required, not decorative -- a disabled MUI
              IconButton has pointer-events:none, so without it the tooltip explaining WHY the
              button is dead would itself never appear on hover. */}
          <Tooltip title={!canWritePricing ? NO_PRICING_WRITE_MESSAGE : "Delete Entry"}>
            <span>
              <IconButton
                color="error"
                size="small"
                disabled={!canWritePricing}
                onClick={() => handleDeleteEntry(params.data)}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
      sortable: false,
      filter: false,
      getQuickFilterText: ''
    },
    {
      headerName: "Broker",
      headerTooltip: "Broker name or identifier",
      field: "BROKER",
      pinned: 'left',
      minWidth: 150
    },
    {
      headerName: "Price File",
      headerTooltip: "Price file reference",
      field: "PRICE_FILE",
      pinned: 'left',
      minWidth: 150
    },
    {
      headerName: "Source",
      headerTooltip: "Data source",
      field: "SOURCE",
      minWidth: 120
    },
    {
      headerName: "To Email",
      headerTooltip: "Broker email address",
      field: "EMAIL",
      minWidth: 200
    },
    {
      headerName: "Subject",
      headerTooltip: "Email subject or description",
      field: "SUBJECT",
      minWidth: 200
    },
    {
      headerName: "Security List",
      headerTooltip: "List of securities",
      field: "SECURITY_LIST",
      minWidth: 200
    },
    {
      headerName: "Additions",
      headerTooltip: "Additional information",
      field: "ADDITIONS",
      minWidth: 150
    },
    {
      headerName: "CUSIPs",
      headerTooltip: "CUSIP identifiers (highlighted in red if duplicates exist)",
      field: "CUSIPS",
      minWidth: 200,
      cellClass: getCusipCellClass
    },
    {
      headerName: "Modified By",
      headerTooltip: "User who last modified this entry",
      field: "MODIFIED_BY",
      editable: false,
      minWidth: 120
    },
    {
      headerName: "Modified Date",
      headerTooltip: "Date when this entry was last modified",
      field: "MODIFIED_DATE",
      editable: false,
      cellRenderer: DateTimeRenderer,
      minWidth: 160
    }
  ], [handleViewHistory, handleDuplicateEntry, handleDeleteEntry, getCusipCellClass, canWritePricing]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    editable: !isLoading,
    cellClass: getCellClass,
    cellStyle: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider
    },
    menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
    lockPinned: false
  }), [theme, isLoading, getCellClass]);

  // Grid options are now handled internally by PamGrid

  // AG Grid modules are now handled internally by PamGrid

  const handleBlankRow = useCallback(() => {
    const nextRowCount = newRowCount + 1;
    setNewRowCount(nextRowCount);

    const newRow = {};
    // Add temporary tracking field that won't change
    newRow['TEMP_ROW_ID'] = `temp_${nextRowCount}_${Date.now()}`;

    columnDefs.forEach(def => {
      const col = def.field;
      if (col === "BROKER") {
        newRow[col] = `new_row_${nextRowCount}`;
      } else if (col === "PRICE_FILE") {
        newRow[col] = `new_file_${nextRowCount}`;
      } else if (col === "MODIFIED_BY" || col === "MODIFIED_DATE") {
        // A new key: null MODIFIED_DATE token; the writer stamps both on save.
        newRow[col] = null;
      } else if (col === "SECURITY_LIST") {
        newRow[col] = true; // Initialize with default value
      } else if (col === "ADDITIONS") {
        newRow[col] = false; // Initialize with default value
      } else {
        newRow[col] = '';
      }
    });

    setRowData(prev => [newRow, ...prev]);
  }, [columnDefs, newRowCount]);

  const onCellValueChanged = useCallback((params) => {
    const { data, colDef, newValue, oldValue } = params;
    const field = colDef.field;

    // AG Grid has already written newValue into data, so the key the row had
    // before this edit comes from oldValue. A loaded row's first key edit gives
    // it a KEY_EDIT_ID to be tracked by from then on: its new key may be one
    // another row already has, and the two rows' edits must stay apart.
    const isKeyEdit = BROKER_KEY_FIELDS.includes(field);
    const rowBefore = { ...data, [field]: oldValue };
    const previousKey = brokerTrackingKey(rowBefore);
    if (isKeyEdit) assignKeyEditId(data);
    const trackingKey = brokerTrackingKey(data);

    // Update row data immediately. An edited key names a different row, so it
    // no longer has a loaded MODIFIED_DATE: the save sends null for it, unless
    // the edit takes the row back to its own key.
    const keyModifiedDate = isKeyEdit && !data.TEMP_ROW_ID
      ? keyEditModifiedDate(loadedKeysRef.current, { rowBefore, rowAfter: data, keyFields: BROKER_KEY_FIELDS, previousKey, trackingKey })
      : null;
    setRowData(prev => prev.map(row => {
      if (row === data) {
        const updatedRow = {
          ...row,
          [field]: newValue,
        };
        return isKeyEdit ? { ...updatedRow, MODIFIED_DATE: keyModifiedDate } : updatedRow;
      }
      return row;
    }));

    // Track the change for highlighting and saving. A first key edit moves the
    // row's earlier edits to its KEY_EDIT_ID.
    setUpdatedRows(prev => trackCellEdit(prev, { previousKey, trackingKey, field, oldValue, newValue }));
  }, []);



  const getUpdatedRowCount = useCallback(() => {
    return Object.keys(updatedRows).length;
  }, [updatedRows]);



  // Validation function for broker data
  const validateBrokerRow = useCallback((row) => {
    const errors = [];

    // Required field validations
    if (!row.BROKER || row.BROKER.trim() === '') {
      errors.push('BROKER is required');
    }
    if (!row.PRICE_FILE || row.PRICE_FILE.trim() === '') {
      errors.push('PRICE_FILE is required');
    }

    return errors;
  }, []);


  // Save modified rows
  const handleSave = useCallback(async () => {
    if (isSavingRef.current) return;
    // Invalidate any pending clear before validation or the request can replace
    // the banner.
    clearActionError();

    // First, validate all rows and collect validation errors
    const validationErrors = [];
    const modifiedRowsData = [];

    Object.keys(updatedRows).forEach(trackingKey => {
      // Find the current row data in the grid by its tracking key
      const currentRowData = rowData.find(row => brokerTrackingKey(row) === trackingKey);

      if (!currentRowData) {
        console.warn(`Could not find row data for tracking key: ${trackingKey}`);
        return;
      }

      // Validate the row
      const rowErrors = validateBrokerRow(currentRowData);
      if (rowErrors.length > 0) {
        const rowIdentifier = currentRowData.TEMP_ROW_ID ?
          `New row` :
          `Row (${currentRowData.BROKER} - ${currentRowData.PRICE_FILE})`;
        validationErrors.push(`${rowIdentifier}: ${rowErrors.join(', ')}`);
        return;
      }

      // A copy of the row carrying its loaded MODIFIED_DATE unchanged (null for a new row).
      // core's helper drops MODIFIED_BY, but /save-brokers hashes the whole row, MODIFIED_BY
      // included, against the latest version to skip unchanged rows, so the loaded value goes
      // back on. The writer still stamps the caller.
      let rowToSave = { ...withLoadedModifiedDate(currentRowData), MODIFIED_BY: currentRowData.MODIFIED_BY };

      // Normalize boolean fields
      rowToSave.ADDITIONS = convertToActualBoolean(rowToSave.ADDITIONS);
      rowToSave.SECURITY_LIST = convertToActualBoolean(rowToSave.SECURITY_LIST);

      // Untouched loaded blanks go back as loaded (null stays null); edited
      // fields, including an explicit false, are sent as the user left them.
      rowToSave = restoreBlankValues(rowToSave, updatedRows[trackingKey]);

      // MODIFIED_BY / MODIFIED_DATE are never restamped here: MODIFIED_DATE is
      // the concurrency token and the writer stamps both.

      // Remove virtual and client-only fields (actions, TEMP_ROW_ID, `_`-prefixed)
      rowToSave = withoutClientFields(rowToSave);

      modifiedRowsData.push(rowToSave);
    });

    // If there are validation errors, show them and stop
    if (validationErrors.length > 0) {
      showTemporaryActionError(`Validation errors:\n${validationErrors.join('\n')}`, 10000);
      return;
    }

    // Two rows under one BROKER|PRICE_FILE would be two writes to one writer
    // row, the later silently replacing the earlier. Check every row on
    // screen, unchanged and new ones included, and save nothing until the user
    // resolves it.
    const duplicates = findDuplicateKeys({
      rows: rowData,
      keyOf: brokerSaveKey,
      isPending: row => Boolean(updatedRows[brokerTrackingKey(row)]),
    });
    if (duplicates.length > 0) {
      showActionError(duplicateKeysMessage(duplicates, {
        keyLabel: 'Broker + Price File',
        describe: row => `${row.BROKER} / ${row.PRICE_FILE}`,
      }));
      return;
    }

    const modifiedRows = modifiedRowsData.map(row => convertEmptyDatesToNull(row));

    if (modifiedRows.length === 0) {
      showTemporaryActionError('No changes to save.', 3000);
      return;
    }

    isSavingRef.current = true;
    // Disables Save and cell editing until the save and its reload finish.
    setIsLoading(true);
    try {
      clearActionError();
      setSuccessMessage(null);
      await saveBrokers(modifiedRows);

      // Reload the grid data to reflect any server-side changes
      await loadBrokers(false, false); // preserveLocalChanges=false, silent=false

      // Ensure updatedRows is cleared and no new rows remain after reload completes
      setUpdatedRows({});

      // Remove any remaining new rows with TEMP_ROW_ID (safety check)
      setRowData(prev => prev.filter(row => !row.TEMP_ROW_ID));

      setSuccessMessage(`Successfully saved ${modifiedRows.length} modified rows.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Error saving broker data:', error);
      if (isPermissionDenied(error)) {
        showActionError(getPermissionDeniedText(error), error);
      } else {
        showActionError(getWriteErrorMessage(error, 'Failed to save changes. Please try again.'));
      }
      if (isVersionConflict(error)) {
        // Reload, keeping the unsaved edits visible. Silent, so the conflict
        // message stays on the banner. Never retry with a refreshed MODIFIED_DATE.
        await loadBrokers(true, true, { adoptServerModifiedDates: true });
      }
    } finally {
      isSavingRef.current = false;
      setIsLoading(false);
    }
  }, [updatedRows, rowData, convertEmptyDatesToNull, loadBrokers, validateBrokerRow, convertToActualBoolean, clearActionError, showActionError, showTemporaryActionError]);

  // Use 1823-core's exportToExcel with custom params
  const handleExportExcel = useCallback(() => {
    exportToExcel({
      fileName: `broker-entries-${new Date().toISOString().split('T')[0]}.xlsx`,
      author: 'PAM Broker System',
      sheetName: 'Broker Entries',
      columnKeys: ['BROKER', 'PRICE_FILE', 'SOURCE', 'EMAIL', 'SUBJECT', 'SECURITY_LIST', 'ADDITIONS', 'CUSIPS', 'MODIFIED_BY', 'MODIFIED_DATE'],
      processCellCallback: (params) => {
        // Force BROKER and PRICE_FILE fields to be exported as text to preserve formatting
        if (params.column.colId === 'BROKER' || params.column.colId === 'PRICE_FILE') {
          return params.value ? `'${params.value.toString()}` : '';
        }
        return params.value;
      },
      customHeader: [
        [{ data: { value: 'Broker Entries Export', type: 'String' } }],
        [],
        [{ data: { value: `Export Date: ${new Date().toLocaleDateString()}`, type: 'String' } }],
        [{ data: { value: `Total Records: ${rowData.length}`, type: 'String' } }],
        []
      ]
    });
  }, [exportToExcel, rowData.length]);

  const handleExportCSV = useCallback(() => {
    if (gridRef.current) {
      // Get the current date for filename
      const dateStr = formatDate(new Date().toISOString().split('T')[0]).replace(/\//g, '-');

      gridRef.current.api.exportDataAsCsv({
        fileName: `broker-entries-${dateStr}.csv`,
        processCellCallback: (params) => {
          // Force BROKER and PRICE_FILE fields to be exported as Excel-safe text
          if (params.column.colId === 'BROKER' || params.column.colId === 'PRICE_FILE') {
            return `="${params.value || ''}"`;  // Excel-safe text format
          }
          // Default processing for other fields
          return params.value;
        }
      });
    }
  }, [gridRef]);

  const handleDownloadTemplate = useCallback(() => {
    // Create template data from imported sample data with any dynamic processing
    const templateData = sampleBrokerData.map(sample => ({
      ...sample
      // Add any dynamic fields here if needed in the future
    }));

    // Create a temporary grid instance for export
    if (gridRef.current) {
      // Store current row data
      const originalRowData = rowData;

      // Temporarily set template data
      setRowData(templateData);

      // Small delay to ensure data is set before export
      setTimeout(() => {
        gridRef.current.api.exportDataAsExcel({
          fileName: 'broker-entries-template.xlsx',
          author: 'PAM Broker System',
          sheetName: 'Broker Template',
          columnKeys: ['BROKER', 'PRICE_FILE', 'SOURCE', 'EMAIL', 'SUBJECT', 'SECURITY_LIST', 'ADDITIONS', 'CUSIPS'],
          skipColumnHeaders: false,
          processCellCallback: (params) => {
            // Force BROKER and PRICE_FILE fields to be exported as text to preserve formatting
            if (params.column.colId === 'BROKER' || params.column.colId === 'PRICE_FILE') {
              // Prepend apostrophe to force Excel to treat as text
              return params.value ? `'${params.value.toString()}` : '';
            }
            // Default processing for other fields
            return params.value;
          },
          customHeader: [
            [
              {
                data: {
                  value: 'Broker Entries Upload Template',
                  type: 'String'
                }
              }
            ],
            [],
            [
              {
                data: {
                  value: 'Instructions: Fill in your broker data below. Delete the sample rows and add your own data.',
                  type: 'String'
                }
              }
            ],
            [
              {
                data: {
                  value: 'Required fields: BROKER, PRICE_FILE',
                  type: 'String'
                }
              }
            ],
            [
              {
                data: {
                  value: 'SECURITY_LIST and ADDITIONS values: Y (Yes) or N (No)',
                  type: 'String'
                }
              }
            ],
            []
          ]
        });

        // Restore original data after export
        setRowData(originalRowData);
      }, 100);
    }
  }, [rowData, gridRef]);

  // normalizeColumnNameForBroker is now defined at module level using 1823-core's createColumnNormalizer

  // Helper function to normalize boolean values for SECURITY_LIST and ADDITIONS
  const normalizeBooleanValue = useCallback((value) => {
    if (isValueNull(value)) return 'N';

    const stringValue = value.toString().toLowerCase().trim();

    // Truthy values that should resolve to Y
    const truthyValues = ['true', 'yes', 'y', '1', 'on', 'enabled', 'active'];

    if (truthyValues.includes(stringValue) || value === true || value === 1) {
      return 'Y';
    }

    return 'N';
  }, []);

  // Helper function to check if a row is completely empty
  const isRowEmpty = useCallback((row) => {
    return Object.values(row).every(value =>
      isValueNull(value) || (typeof value === 'string' && value.trim() === '')
    );
  }, []);

  // Process uploaded file data
  const processUploadedData = useCallback((rawData) => {
    if (!rawData || rawData.length === 0) {
      throw new Error('No data found in uploaded file');
    }

    // Filter out completely empty rows first
    const nonEmptyRows = rawData.filter((row) => {
      return !isRowEmpty(row);
    });

    if (nonEmptyRows.length === 0) {
      throw new Error('No valid data rows found in the uploaded file (all rows are empty)');
    }

    const processedData = nonEmptyRows.map((row, index) => {
      // Normalize all column keys using 1823-core normalizeRowKeys
      const normalizedRow = normalizeRowKeys(row, normalizeColumnNameForBroker);
      const processedRow = {};

      // Apply field-specific transforms to the normalized row
      Object.keys(normalizedRow).forEach(normalizedKey => {
        const value = normalizedRow[normalizedKey];

        if (normalizedKey === 'VERSION' || normalizedKey === 'MODIFIED_BY' || normalizedKey === 'MODIFIED_DATE') {
          // MODIFIED_DATE comes from the matched loaded row (null for a new key),
          // never the file; the writer stamps MODIFIED_BY.
          return;
        }

        if (normalizedKey === 'SECURITY_LIST' || normalizedKey === 'ADDITIONS') {
          processedRow[normalizedKey] = normalizeBooleanValue(value);
        } else if (normalizedKey === 'BROKER' || normalizedKey === 'PRICE_FILE') {
          // Always convert BROKER and PRICE_FILE fields to strings to preserve formatting (e.g., leading zeros)
          let stringValue = isValueNull(value) ? '' : String(value);

          // Handle CSV export format: strip =" and " wrapper if present
          if (stringValue.startsWith('="') && stringValue.endsWith('"')) {
            stringValue = stringValue.slice(2, -1); // Remove =" from start and " from end
          }

          // Handle Excel apostrophe prefix: strip leading apostrophe if present
          if (stringValue.startsWith("'")) {
            stringValue = stringValue.slice(1); // Remove ' from start
          }

          processedRow[normalizedKey] = stringValue;
        } else {
          // Handle other fields as strings
          processedRow[normalizedKey] = isValueNull(value) ? '' : value.toString();
        }
      });

      // Add temporary tracking ID for new uploaded rows
      processedRow.TEMP_ROW_ID = `upload_${Date.now()}_${index}`;


      return processedRow;
    });

    return processedData;
  }, [isRowEmpty, normalizeBooleanValue]);

  // Helper function to merge two rows with precedence rules
  const mergeRows = useCallback((existingRow, uploadedRow) => {
    const mergedRow = { ...existingRow };

    // Get all fields from uploaded row (excluding tracking fields)
    Object.keys(uploadedRow).forEach(field => {
      if (field === 'TEMP_ROW_ID') return; // Skip tracking field

      const existingValue = existingRow[field];
      const uploadedValue = uploadedRow[field];

      // Skip system fields - they should be preserved from existing row unless business changes are detected
      if (field === 'MODIFIED_BY' || field === 'MODIFIED_DATE') {
        return; // Keep existing values
      }

      // Apply precedence rules:
      // 1. Non-null values take precedence over null/empty values
      // 2. Uploaded values take precedence when both are non-null

      const existingIsEmpty = isValueNull(existingValue);
      const uploadedIsEmpty = isValueNull(uploadedValue);

      if (existingIsEmpty && !uploadedIsEmpty) {
        // Use uploaded value when existing is empty
        mergedRow[field] = uploadedValue;
      } else if (!existingIsEmpty && uploadedIsEmpty) {
        // Keep existing value when uploaded is empty
        mergedRow[field] = existingValue;
      } else if (!existingIsEmpty && !uploadedIsEmpty) {
        // Both have values - uploaded takes precedence
        mergedRow[field] = uploadedValue;
      }
      // If both are empty, keep existing (which is already in mergedRow)
    });

    return mergedRow;
  }, []);

  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      clearActionError();
      setIsLoading(true);

      // Use 1823-core parseFileData to handle both CSV and Excel files
      const rawData = await parseFileData(file, { cellDates: false, filterEmptyRows: true, preserveFalsy: true });

      if (rawData.length === 0) {
        throw new Error('No data rows found in the uploaded file.');
      }

      // Process the raw data
      const processedData = processUploadedData(rawData);

      // Validate required fields
      const validationErrors = [];
      processedData.forEach((row, index) => {
        const rowErrors = validateBrokerRow(row);
        if (rowErrors.length > 0) {
          validationErrors.push(`Row ${index + 1}: ${rowErrors.join(', ')}`);
        }
      });

      if (validationErrors.length > 0) {
        throw new Error(`Validation errors found:\n${validationErrors.join('\n')}`);
      }

      // Merge uploaded data with the grid as it is now, after the parse: the
      // rows this callback's closure captured may predate a background refresh
      // that reordered them, and indices into those would land on other rows.
      // The merged array is built here and set as is.
      const baseRows = rowDataRef.current;
      const newRows = [];
      const updatedRows = [];
      const unchangedRows = [];
      const uploadTrackingEntries = {};

      processedData.forEach((uploadedRow, index) => {
        const uploadedBroker = uploadedRow.BROKER;
        const uploadedPriceFile = uploadedRow.PRICE_FILE;

        // Find existing row with matching broker/PRICE_FILE
        const existingRowIndex = baseRows.findIndex(existingRow =>
          existingRow.BROKER === uploadedBroker && existingRow.PRICE_FILE === uploadedPriceFile
        );

        if (existingRowIndex >= 0) {
          // Row exists - merge it
          const existingRow = baseRows[existingRowIndex];
          const mergedRow = mergeRows(existingRow, uploadedRow);

          // Track changes for the existing row under its tracking key
          const compositeKey = brokerTrackingKey(existingRow);
          const changes = {};

          // Compare merged row with original existing row to track changes
          // Exclude system fields when checking for business data changes
          const businessFields = Object.keys(mergedRow).filter(field =>
            field !== 'TEMP_ROW_ID' && field !== 'MODIFIED_BY' && field !== 'MODIFIED_DATE'
          );

          let hasBusinessChanges = false;
          businessFields.forEach(field => {
            if (mergedRow[field] !== existingRow[field]) {
              // Check for equivalent values for boolean fields
              if (field === 'SECURITY_LIST' || field === 'ADDITIONS') {
                const existingNormalized = normalizeBooleanValue(existingRow[field]);
                const mergedNormalized = normalizeBooleanValue(mergedRow[field]);
                if (existingNormalized !== mergedNormalized) {
                  changes[field] = {
                    oldValue: existingRow[field],
                    newValue: mergedRow[field]
                  };
                  hasBusinessChanges = true;
                }
              } else {
                // For non-boolean fields, do basic equivalence check
                const existingStr = (existingRow[field] || '').toString().trim();
                const mergedStr = (mergedRow[field] || '').toString().trim();
                if (existingStr !== mergedStr) {
                  changes[field] = {
                    oldValue: existingRow[field],
                    newValue: mergedRow[field]
                  };
                  hasBusinessChanges = true;
                }
              }
            }
          });

          if (hasBusinessChanges) {
            // MODIFIED_BY / MODIFIED_DATE stay as loaded: the save sends the
            // loaded MODIFIED_DATE and the writer stamps both.

            // Normalize boolean fields to actual boolean values for UI
            if (mergedRow['SECURITY_LIST'] !== undefined) {
              mergedRow['SECURITY_LIST'] = convertToActualBoolean(mergedRow['SECURITY_LIST']);
            }
            if (mergedRow['ADDITIONS'] !== undefined) {
              mergedRow['ADDITIONS'] = convertToActualBoolean(mergedRow['ADDITIONS']);
            }

            uploadTrackingEntries[compositeKey] = changes;
            updatedRows.push({ index: existingRowIndex, row: mergedRow });
          } else {
            // No business changes - keep original row unchanged but normalize boolean fields for UI
            const normalizedExistingRow = { ...existingRow };
            if (normalizedExistingRow['SECURITY_LIST'] !== undefined) {
              normalizedExistingRow['SECURITY_LIST'] = convertToActualBoolean(normalizedExistingRow['SECURITY_LIST']);
            }
            if (normalizedExistingRow['ADDITIONS'] !== undefined) {
              normalizedExistingRow['ADDITIONS'] = convertToActualBoolean(normalizedExistingRow['ADDITIONS']);
            }

            unchangedRows.push({ index: existingRowIndex, row: normalizedExistingRow });
          }
        } else {
          // New row: no loaded MODIFIED_DATE. Normalize boolean fields for UI.
          const newRow = {
            ...uploadedRow,
            MODIFIED_BY: null,
            MODIFIED_DATE: null
          };

          // Normalize boolean fields to actual boolean values for UI
          if (newRow['SECURITY_LIST'] !== undefined) {
            newRow['SECURITY_LIST'] = convertToActualBoolean(newRow['SECURITY_LIST']);
          }
          if (newRow['ADDITIONS'] !== undefined) {
            newRow['ADDITIONS'] = convertToActualBoolean(newRow['ADDITIONS']);
          }

          newRows.push(newRow);

          // Track as new row
          uploadTrackingEntries[uploadedRow.TEMP_ROW_ID] = {
            ...Object.keys(newRow).reduce((acc, field) => {
              if (field !== 'TEMP_ROW_ID') {
                acc[field] = { oldValue: '', newValue: newRow[field] };
              }
              return acc;
            }, {})
          };


        }
      });

      // Update row data: apply updates to the rows the indices came from, then
      // add new rows at the top
      const updatedGrid = [...baseRows];
      updatedRows.forEach(({ index, row }) => {
        updatedGrid[index] = row;
      });
      setRowData([...newRows, ...updatedGrid]);

      // Update tracking for modified rows
      setUpdatedRows(prev => addTrackedChanges(prev, uploadTrackingEntries));

      const newCount = newRows.length;
      const updatedCount = updatedRows.length;
      const unchangedCount = unchangedRows.length;

      let message = `Successfully processed ${processedData.length} entries: `;
      const parts = [];

      if (newCount > 0) parts.push(`${newCount} new`);
      if (updatedCount > 0) parts.push(`${updatedCount} updated`);
      if (unchangedCount > 0) parts.push(`${unchangedCount} already existing (no changes)`);

      message += parts.join(', ');
      message += '. Review the data and click Save to persist changes.';

      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 8000);

    } catch (error) {
      console.error('Upload error:', error);
      showTemporaryActionError(
        error.message || 'Failed to process uploaded file. Please check the format and try again.',
        8000
      );
    } finally {
      setIsLoading(false);
      // Clear the file input
      event.target.value = '';
    }
  }, [processUploadedData, validateBrokerRow, mergeRows, normalizeBooleanValue, convertToActualBoolean, clearActionError, showTemporaryActionError]);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <Box>
      {/* Hidden file input for upload functionality */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Broker Management</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          {/* Per-user saved column layouts (view-config) */}
          <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />
          <Tooltip title="Upload broker entries from CSV or Excel files">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightblue",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(173, 216, 230, 0.8)'
                  }
                }}
                startIcon={<UploadIcon />}
                onClick={handleUploadClick}
                disabled={isLoading}
              >
                Upload Entries
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Download Excel template file with sample broker data">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightcyan",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(224, 255, 255, 0.8)'
                  }
                }}
                onClick={handleDownloadTemplate}
                startIcon={<DownloadIcon />}
                disabled={isLoading}
              >
                Template
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export current broker entries to Excel file">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightgreen",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(144, 238, 144, 0.8)'
                  }
                }}
                onClick={handleExportExcel}
                startIcon={<DownloadIcon />}
                disabled={isLoading}
              >
                Export Excel
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export current broker entries to CSV file">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightyellow",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 224, 0.8)'
                  }
                }}
                onClick={handleExportCSV}
                startIcon={<DownloadIcon />}
                disabled={isLoading}
              >
                Export CSV
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Add a new blank row to create a new broker entry">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightgrey",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)'
                  }
                }}
                onClick={handleBlankRow}
                disabled={isLoading}
              >
                Add Blank Row
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={!canWritePricing ? NO_PRICING_WRITE_MESSAGE : `Save ${getUpdatedRowCount() > 0 ? `${getUpdatedRowCount()} changed` : 'all'} broker entries`}>
            <span>
              <Button
                sx={{
                  backgroundColor: getUpdatedRowCount() > 0 ? "orange" : "lightgrey",
                  color: "black",
                  '&:hover': {
                    backgroundColor: getUpdatedRowCount() > 0 ? 'rgba(255, 165, 0, 0.8)' : 'rgba(0, 0, 0, 0.1)'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)'
                  }
                }}
                onClick={handleSave}
                disabled={!canWritePricing || isLoading || getUpdatedRowCount() === 0}
              >
                {isLoading ? 'Saving...' : `Save${getUpdatedRowCount() > 0 ? ` (${getUpdatedRowCount()})` : ''}`}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {refreshFailed && (
        <Typography color="warning.main" variant="body2" sx={{ mb: 1 }}>
          Live refresh failed — the data below may be out of date.
        </Typography>
      )}
      {loadError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {loadError}
        </Typography>
      )}
      {error && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography color="error">
            {error}
          </Typography>
          {deniedError && <RequestAccessButton error={deniedError} />}
        </Box>
      )}
      {successMessage && (
        <Typography color="success.main" sx={{ mb: 2 }}>
          {successMessage}
        </Typography>
      )}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2">Loading brokers...</Typography>
        </Box>
      )}
      <Box sx={{ mb: 2 }}>
        <QuickFilter
          value={quickFilterText}
          onChange={setQuickFilterText}
          placeholder="Search brokers..."
        />
      </Box>
      <style>{`
        .ag-cell-range-selected {
          background-color: rgba(120, 160, 240, 0.3) !important;
          border: 2px solid #3f7cac !important;
        }
        .ag-cell-range-selected-1 {
          background-color: rgba(120, 160, 240, 0.3) !important;
        }
        .ag-cell-range-selected-2 {
          background-color: rgba(120, 160, 240, 0.2) !important;
        }
        .ag-cell-range-selected-3 {
          background-color: rgba(120, 160, 240, 0.1) !important;
        }
        .ag-cell-range-selected-4 {
          background-color: rgba(120, 160, 240, 0.1) !important;
        }
        .ag-cell-focus {
          border: 2px solid #3f7cac !important;
          background-color: rgba(120, 160, 240, 0.4) !important;
        }
        .ag-cell-updated {
          background-color: rgba(255, 165, 0, 0.2) !important;
        }
        .ag-cell-updated-new-row {
          background-color: rgba(255, 200, 100, 0.3) !important;
        }
        .ag-cell-duplicate-cusips {
          background-color: rgba(255, 0, 0, 0.2) !important;
          border: 1px solid #ff0000 !important;
          font-weight: 500;
        }
      `}</style>
      <PamGrid
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        quickFilterText={quickFilterText}
        height="calc(80vh - 300px)"
        isLoading={isLoading}
        loadingMessage="Loading brokers..."

        // Row identification: the tracking key, unique even while two rows
        // share a BROKER|PRICE_FILE (AG Grid would otherwise show only one).
        getRowId={(params) => brokerTrackingKey(params.data ?? params)}

        // Cell editing
        onCellValueChanged={onCellValueChanged}

        // Grid layout persistence: bridge grid-ready so the saved default
        // applies once the grid mounts, and mirror every column/sort/filter
        // gesture into the local stash so unsaved tweaks survive a refresh.
        onGridReady={gridConfigs.onGridReady}
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={gridConfigs.stashBindings}

        // Grid options
        rowHeight={32}
        enableCellTextSelection={true}
        enableRangeSelection={true}
        ensureDomOrder={true}
        undoRedoCellEditing={true}
        suppressHorizontalScroll={false}

        // Footer with copy/paste instructions
        showFooter={true}
      />

      {/* Copy/paste instructions are now shown via PamGrid's showFooter prop */}

      {/* Color Legend */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: theme.palette.grey[50], borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
          Color Legend
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: 'rgba(255, 0, 0, 0.2)',
                border: '1px solid #ff0000',
                borderRadius: 1
              }}
            />
            <Typography variant="body2">
              <strong>Duplicate CUSIPs:</strong> CUSIP list contains duplicate identifiers
            </Typography>
          </Box>
        </Box>
      </Box>

      <HistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={`Broker Entry History - ${selectedEntryForHistory?.BROKER || selectedEntryForHistory?.EMAIL || 'Entry'}`}
        height="60vh"
      >
        <BrokerHistory
          broker={selectedEntryForHistory?.BROKER}
          priceFile={selectedEntryForHistory?.PRICE_FILE}
          showTitle={false}
        />
      </HistoryModal>

      <ConfirmationDialog
        open={confirmationModalOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Entry"
        message={`Are you sure you want to delete this broker entry for "${entryToDelete?.BROKER}" with price file "${entryToDelete?.PRICE_FILE}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </Box>
  );
};

export default BrokersGrid;
